import * as React from "react";
const { useState } = React;
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetDescription,
  SheetFooter
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { 
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { QrCode, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { UserApi } from "@/api/user.api";

interface MFASetupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  qrCodeData?: string | null;
  username?: string | null;
  onMfaStatusChange?: (mfaEnabled: boolean) => void;
}

export const MFASetupSheet: React.FC<MFASetupSheetProps> = ({
  open,
  onOpenChange,
  userId,
  userName,
  qrCodeData,
  username,
  onMfaStatusChange
}) => {
  const [step, setStep] = useState<'qr' | 'verify' | 'success'>('qr');
  const [otp, setOtp] = useState("");
  
  const handleVerify = async () => {
    if (otp.length < 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    if (!username) {
      toast.error("User information not found");
      return;
    }
    try {
      await UserApi.verifyMfaQrCode({ username, mfaCode: otp });
      setStep('success');
      toast.success("MFA enabled successfully!");
      // Fetch latest mfaEnabled status
      const userRes = await UserApi.getUser({ username });
      if (userRes?.data?.mfaEnabled !== undefined && onMfaStatusChange) {
        onMfaStatusChange(!!userRes.data.mfaEnabled);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to verify MFA code");
    }
  };
  
  const handleClose = () => {
    // Reset the sheet state when closing
    setStep('qr');
    setOtp("");
    onOpenChange(false);
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader className="mb-6">
          <SheetTitle>Enable Multi-Factor Authentication</SheetTitle>
          <SheetDescription>
            Secure your account with an additional layer of protection
          </SheetDescription>
        </SheetHeader>
        
        {step === 'qr' && (
          <div className="space-y-6">
            <div className="text-sm text-gray-600">
              <p className="mb-4">Scan this QR code with your authenticator app:</p>
              <div className="mx-auto w-48 h-48 bg-gray-50 border flex items-center justify-center">
                {qrCodeData ? (
                  <img src={qrCodeData} alt="MFA QR Code" className="w-40 h-40 object-contain" />
                ) : (
                  <div className="border-4 border-gray-300 p-6 rounded-lg bg-white">
                    <QrCode size={100} className="text-gray-800" />
                  </div>
                )}
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Make sure to save your recovery codes in a secure place. You'll need them if you lose access to your device.
              </p>
            </div>
            
            <Button 
              className="w-full" 
              onClick={() => setStep('verify')}
            >
              I've scanned the QR code <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}
        
        {step === 'verify' && (
          <div className="space-y-6">
            <div>
              <p className="mb-5 text-sm text-gray-600">
                Enter the 6-digit code from your authenticator app to verify:
              </p>
              
              <div className="flex justify-center mb-6">
                <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            
            <SheetFooter>
              <Button 
                variant="outline" 
                onClick={() => setStep('qr')}
              >
                Back
              </Button>
              <Button onClick={handleVerify}>
                Verify and Enable MFA
              </Button>
            </SheetFooter>
          </div>
        )}
        
        {step === 'success' && (
          <div className="flex flex-col items-center justify-center space-y-4 py-8">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-xl font-medium text-center">MFA Enabled Successfully!</h3>
            <p className="text-center text-gray-600 mb-4">
              Your account is now secured with multi-factor authentication.
            </p>
            <Button onClick={handleClose}>
              Close
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
